import { useEffect, useRef, useState } from "react";
import { KeyRound, Flame, Users, Target, Vault, Siren } from "lucide-react";
import { vaultAlarm } from "@/lib/vaultSounds";

const fmt = (n) => Number(n || 0).toLocaleString("de-DE");

function usePot(pot) {
  const [n, setN] = useState(pot);
  const [gain, setGain] = useState(null);
  const prev = useRef(pot);
  useEffect(() => {
    const from = prev.current;
    prev.current = pot;
    if (pot === from) return;
    if (pot > from) setGain({ v: pot - from, k: Date.now() });
    let raf, t0;
    const tick = (t) => { t0 ??= t; const p = Math.min(1, (t - t0) / 700); setN(Math.round(from + (pot - from) * (1 - Math.pow(1 - p, 3)))); if (p < 1) raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [pot]);
  return [n, gain];
}

function Keys({ keys }) {
  const prev = useRef(keys.left);
  const [used, setUsed] = useState(null);
  useEffect(() => { if (keys.left < prev.current) setUsed(keys.left); prev.current = keys.left; }, [keys.left]);
  return (
    <div className="flex items-center gap-1.5" data-testid="vault-keys">
      {Array.from({ length: keys.total }).map((_, i) => (
        <span key={i} className={`w-8 h-8 rounded-xl grid place-items-center transition-colors ${i === used ? "vault-key-use" : ""} ${i < keys.left ? "bg-amber-400 text-slate-900 shadow-[0_0_14px_rgba(251,191,36,0.6)]" : "bg-white/10 text-white/30"}`}>
          <KeyRound className="w-4 h-4" />
        </span>
      ))}
      <span className="ml-1 text-sm font-extrabold text-amber-200" data-testid="vault-keys-left">{keys.left} übrig</span>
    </div>
  );
}

export function VaultHeader({ s }) {
  const r = s.round;
  const [pot, gain] = usePot(r.pot);
  useEffect(() => {
    const k = `vault-alarm-${r.no}`;
    if (r.best >= 3 && !sessionStorage.getItem(k)) { sessionStorage.setItem(k, "1"); vaultAlarm(); }
  }, [r.best, r.no]);
  const pct = Math.min(100, Math.round((r.pot / r.max_pot) * 100));
  return (
    <div className="relative overflow-hidden rounded-3xl bg-slate-950 text-white p-5 sm:p-6 ring-1 ring-amber-400/30" data-testid="vault-header">
      <div className="absolute -top-24 -right-16 w-72 h-72 rounded-full bg-amber-500/20 blur-3xl pointer-events-none" />
      <div className="absolute inset-0 vault-sheen pointer-events-none" />
      {r.best >= 3 && <div className="absolute inset-x-0 top-0 h-1 bg-rose-500 vault-beacon pointer-events-none" />}
      <div className="relative flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-amber-300 flex items-center gap-1.5"><Vault className="w-4 h-4" /> Goldener Tresor #{r.no}</p>
          <div className="relative inline-block">
            <p className="mt-2 font-display text-5xl sm:text-6xl font-black text-amber-300 tabular-nums vault-pot" data-testid="vault-pot">{fmt(pot)}</p>
            {gain && <span key={gain.k} className="absolute -right-10 top-2 text-lg font-black text-emerald-300 vault-gain">+{gain.v}</span>}
          </div>
          <p className="text-sm text-white/60">Punkte im Tresor · max. {fmt(r.max_pot)}</p>
        </div>
        {s.streak > 0 && <span className="shrink-0 inline-flex items-center gap-1 px-3 h-8 rounded-full bg-orange-500/20 text-orange-300 text-xs font-extrabold" data-testid="vault-streak"><Flame className="w-4 h-4 fill-orange-500" /> {s.streak} {s.streak === 1 ? "Tag" : "Tage"}</span>}
      </div>
      <div className="relative mt-4 h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-gradient-to-r from-amber-500 to-yellow-300 transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
      <div className="relative mt-4 grid grid-cols-3 gap-2 text-center">
        <div className="rounded-2xl bg-white/5 py-2"><p className="text-lg font-black tabular-nums" data-testid="vault-tries">{fmt(r.tries)}</p><p className="text-[11px] text-white/50">Versuche</p></div>
        <div className="rounded-2xl bg-white/5 py-2"><p className="text-lg font-black tabular-nums flex items-center justify-center gap-1" data-testid="vault-players"><Users className="w-4 h-4 text-white/50" />{r.players}</p><p className="text-[11px] text-white/50">Knacker</p></div>
        <div className={`rounded-2xl py-2 ${r.best >= 3 ? "bg-rose-500/25 animate-pulse" : "bg-white/5"}`}><p className="text-lg font-black flex items-center justify-center gap-1" data-testid="vault-best"><Target className="w-4 h-4 text-white/50" />{r.best}/4</p><p className="text-[11px] text-white/50">Bester Versuch</p></div>
      </div>
      {r.best >= 3 && <p className="relative mt-3 text-sm font-extrabold text-rose-300 flex items-center gap-1.5" data-testid="vault-near-warning"><Siren className="w-4 h-4 vault-siren" /> Achtung: Jemand hat schon 3 von 4 Ziffern richtig!</p>}
      <div className="relative mt-4 flex flex-wrap items-center justify-between gap-3">
        <Keys keys={s.keys} />
        {!s.played_today && s.next_bonus > 0 && <span className="text-xs font-bold text-amber-200/80" data-testid="vault-next-bonus">Erster Versuch heute: +{s.next_bonus} Serien-Bonus</span>}
      </div>
    </div>
  );
}
